import {
  addBreadcrumbsBeforeApiRequest,
  captureApiError,
} from '@/shared/lib/captureApiError'

function initState() {
  return {
    available_pairs: [], // Array of objects, each object represents information about one exchange pair
    fromCoinIdList: [], // Sorted by "sort_priority_wallet" list of coin ids that can be used as source coin for the exchange
    toCoinIdList: [], // Sorted by "sort_priority_wallet" list of coin ids that can be used as target coin for the exchange
    fromCoinsDetails: {}, // Object with coin details, key is coin id, value is coin object
    toCoinsDetails: {}, // Object with coin details, key is coin id, value is coin object
    from_list: {}, // Legacy
    to_list: {}, // Legacy
  }
}

const state = initState()

const mutations = {
  AVAILABLE_PAIRS(state, payload) {
    state.available_pairs = payload
  },
  FROM_COIN_ID_LIST(state, list) {
    state.fromCoinIdList = list
  },
  TO_COIN_ID_LIST(state, list) {
    state.toCoinIdList = list
  },
  FROM_LIST(state, list) {
    state.from_list = list
  },
  TO_LIST(state, list) {
    state.to_list = list
  },
  FROM_COINS_DETAILS(state, payload) {
    state.fromCoinsDetails = payload
  },
  TO_COINS_DETAILS(state, payload) {
    state.toCoinsDetails = payload
  },
}

const actions = {
  async GET_EXCHANGE_PAIRS({ commit, rootState, rootGetters }) {
    try {
      // Get data from the API
      addBreadcrumbsBeforeApiRequest(
        'GET_EXCHANGE_PAIRS',
        '/v2/exchange/pairs',
        {
          subAccountId: rootGetters['auth/isSub']
            ? `/${rootState.auth.accountID}`
            : '',
        }
      )
      const { data } = await this.$apiService().emcdApi.get(
        `/v2/exchange/pairs${rootGetters['auth/isSub'] ? `/${rootState.auth.accountID}` : ''}`
      )
      if (!data && data?.length) return

      // Save information about all available pairs
      const ALL_AVAILABLE_PAIRS = data.filter((i) => i.directions !== 'off')
      commit('AVAILABLE_PAIRS', ALL_AVAILABLE_PAIRS)

      // Get map – full coin information by legacy numeric coin id
      const { coins } = rootState.coinService
      const coinsByLegacyId = {}

      Object.keys(coins).forEach((key) => {
        coinsByLegacyId[coins[key].legacy_coin_id] = coins[key]
      })

      // Get full list of coin ids that can be used as source coin for the exchange
      const FROM_ID_LIST = []
      ALL_AVAILABLE_PAIRS.forEach((i) => {
        if (i.directions === 'sell' && !FROM_ID_LIST.includes(i.base_coin_id))
          FROM_ID_LIST.push(i.base_coin_id)
        else if (
          i.directions === 'buy' &&
          !FROM_ID_LIST.includes(i.quote_coin_id)
        )
          FROM_ID_LIST.push(i.quote_coin_id)
        else if (i.directions === 'both') {
          if (!FROM_ID_LIST.includes(i.base_coin_id))
            FROM_ID_LIST.push(i.base_coin_id)
          if (!FROM_ID_LIST.includes(i.quote_coin_id))
            FROM_ID_LIST.push(i.quote_coin_id)
        }
      })

      FROM_ID_LIST.forEach((value) => {
        if (!coinsByLegacyId[value]) {
          console.error(
            'GET_EXCHANGE_PAIRS Error: Coin not found in coins list',
            ALL_AVAILABLE_PAIRS.filter(
              (i) => i.base_coin_id === value || i.quote_coin_id === value
            ).map((i) => i.id)
          )
        }
      })

      const FROM_COIN_ID_LIST_VALUES = []

      FROM_ID_LIST.forEach((legacyCoinId) => {
        const coin = coinsByLegacyId[legacyCoinId]
        if (coin) {
          FROM_COIN_ID_LIST_VALUES.push(coin)
        }
      })

      commit(
        'FROM_COIN_ID_LIST',
        FROM_COIN_ID_LIST_VALUES.sort(
          (a, b) => a.sort_priority_wallet - b.sort_priority_wallet
        ).map((coin) => coin.id)
      )

      // Get map – full coin information by legacy numeric coin id BUT only for source coins
      const FROM_ID_LIST_VALUES = {}

      FROM_ID_LIST.forEach((key) => {
        if (coinsByLegacyId[key]) {
          FROM_ID_LIST_VALUES[key] = coinsByLegacyId[key]
        }
      })
      commit('FROM_LIST', FROM_ID_LIST_VALUES)

      // Get full list of coin ids that can be used as target coin for the exchange
      const COIN_ID = FROM_ID_LIST[0]
      const TO_ID_LIST = []
      ALL_AVAILABLE_PAIRS.forEach((i) => {
        if (
          i.base_coin_id === COIN_ID &&
          ['both', 'sell'].includes(i.directions)
        )
          TO_ID_LIST.push(i.quote_coin_id)
        if (
          i.quote_coin_id === COIN_ID &&
          ['both', 'buy'].includes(i.directions)
        )
          TO_ID_LIST.push(i.base_coin_id)
      })

      const TO_COIN_ID_LIST_VALUES = []

      TO_ID_LIST.forEach((legacyCoinId) => {
        const coin = coinsByLegacyId[legacyCoinId]
        if (coin) {
          TO_COIN_ID_LIST_VALUES.push(coin)
        }
      })

      commit(
        'TO_COIN_ID_LIST',
        TO_COIN_ID_LIST_VALUES.sort(
          (a, b) => a.sort_priority_wallet - b.sort_priority_wallet
        ).map((coin) => coin.id)
      )

      // Get map – full coin information by legacy numeric coin id BUT only for target coins
      const TO_ID_LIST_VALUES = {}

      TO_ID_LIST.forEach((key) => {
        if (coinsByLegacyId[key]) {
          TO_ID_LIST_VALUES[key] = coinsByLegacyId[key]
        }
      })

      commit('TO_LIST', TO_ID_LIST_VALUES)
    } catch (error) {
      console.error('GET_EXCHANGE_PAIRS Error: ', error)
      captureApiError('GET_EXCHANGE_PAIRS', '/v2/exchange/pairs', error)
    }
  },

  async GET_EXCHANGE_LIMIT({ rootState, rootGetters }, pair) {
    const { data } = await this.$apiService().emcdApi.get(
      `/v1/exchange/limit/${pair}${rootGetters['auth/isSub'] ? `/${rootState.auth.accountID}` : ''}`
    )
    return data
  },

  async GET_EXCHANGE_RATE(
    { rootState, rootGetters },
    { pair, amount, direction }
  ) {
    return new Promise((resolve, reject) => {
      addBreadcrumbsBeforeApiRequest(
        'GET_EXCHANGE_RATE',
        '/v1/exchange/rate/',
        {
          pair,
          subAccountId: rootGetters['auth/isSub']
            ? `/${rootState.auth.accountID}`
            : '',
          amount,
          direction,
        }
      )
      this.$apiService()
        .emcdApi.post(
          `/v1/exchange/rate/${pair}${rootGetters['auth/isSub'] ? `/${rootState.auth.accountID}` : ''}`,
          {
            amount,
            direction,
          },
          {
            headers: {
              'Content-Type': 'application/json',
            },
          }
        )
        .then(({ data }) => {
          resolve(data)
        })
        .catch((e) => {
          captureApiError('GET_EXCHANGE_RATE', '/v1/exchange/rate/', e)
          reject(e.response?.data || e)
        })
    })
  },

  async ORDER_EXCHANGE({ rootState, rootGetters }, pair) {
    try {
      addBreadcrumbsBeforeApiRequest('ORDER_EXCHANGE', '/v1/exchange/order/', {
        pair,
        subAccountId: rootGetters['auth/isSub']
          ? `/${rootState.auth.accountID}`
          : '',
      })
      const data = await this.$apiService().emcdApi.post(
        `/v1/exchange/order/${pair}${rootGetters['auth/isSub'] ? `/${rootState.auth.accountID}` : ''}`
      )
      return data
    } catch (e) {
      captureApiError('ORDER_EXCHANGE', '/v1/exchange/order/', e)
      return e.response?.data || e
    }
  },

  async ON_CHANGE_FROM({ state, commit, rootState }, coinId) {
    // Get a map – full coin information by legacy numeric coin id
    const { coins } = rootState.coinService
    const coinsByLegacyId = {}

    Object.keys(coins).forEach((key) => {
      coinsByLegacyId[coins[key].legacy_coin_id] = coins[key]
    })

    // Update list of coin ids that can be used as target coin for the exchange
    const TO_ID_LIST = []
    state.available_pairs.filter((i) => {
      if (
        i.base_coin_id === +coinId &&
        ['both', 'sell'].includes(i.directions)
      ) {
        TO_ID_LIST.push(i.quote_coin_id)
      }
      if (
        i.quote_coin_id === +coinId &&
        ['both', 'buy'].includes(i.directions)
      ) {
        TO_ID_LIST.push(i.base_coin_id)
      }

      return true
    })
    commit(
      'TO_COIN_ID_LIST',
      TO_ID_LIST.map((legacyCoinId) => coinsByLegacyId[legacyCoinId])
        .sort((a, b) => a.sort_priority_wallet - b.sort_priority_wallet)
        .map((coin) => coin?.id)
    )

    // Update map – full coin information by legacy numeric coin id BUT only for target coins
    const TO_ID_LIST_VALUES = {}

    TO_ID_LIST.forEach((key) => {
      if (coinsByLegacyId[key]) {
        TO_ID_LIST_VALUES[key] = coinsByLegacyId[key]
      }
    })
    commit('TO_LIST', TO_ID_LIST_VALUES)
  },
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
}
